import { toast } from 'react-toastify';
import TokenManager from './tokenManager';

/**
 * Global error handler for API responses
 * Provides consistent error handling and user feedback
 */
export class ErrorHandler {
  /**
   * Handle API errors and show appropriate user messages
   * @param {object} error - Axios error object
   * @param {object} options - Handler options
   * @param {boolean} options.showToast - Whether to show toast notification
   * @param {string} options.fallbackMessage - Fallback error message
   */
  static handle(error, options = {}) {
    const {
      showToast = true,
      fallbackMessage = 'An unexpected error occurred',
    } = options;

    let message = fallbackMessage;
    let type = 'error';

    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      
      switch (status) {
        case 400:
          message = data.message || 'Invalid request. Please check your input.';
          break;
        case 401:
          message = 'You are not authorized. Please log in again.';
          // Redirect to login or refresh token
          this.handleUnauthorized();
          break;
        case 403:
          message = 'You do not have permission to perform this action.';
          break;
        case 404:
          message = 'The requested resource was not found.';
          break;
        case 422:
          message = this.formatValidationErrors(data.errors) || 'Validation failed.';
          break;
        case 429:
          message = 'Too many requests. Please try again later.';
          break;
        case 500:
          message = 'Server error. Please try again later.';
          break;
        case 503:
          message = 'Service temporarily unavailable. Please try again later.';
          break;
        default:
          message = data.message || fallbackMessage;
      }
    } else if (error.request) {
      // Request was made but no response received
      message = 'Network error. Please check your internet connection.';
    } else {
      // Something else happened
      message = error.message || fallbackMessage;
    }

    if (showToast) {
      toast[type](message);
    }

    // Log error for debugging
    console.error('API Error:', error);

    return { message, type, status: error.response?.status };
  }

  /**
   * Handle unauthorized errors (401)
   */
  static handleUnauthorized() {
    // Clear stored tokens using TokenManager
    TokenManager.clearTokens();
    
    // Redirect to login page
    window.location.href = '/sign/login';
  }

  /**
   * Format validation errors from server
   * @param {object|array} errors - Validation errors
   */
  static formatValidationErrors(errors) {
    if (!errors) return null;

    if (Array.isArray(errors)) {
      return errors.map(error => error.message || error).join(', ');
    }

    if (typeof errors === 'object') {
      return Object.values(errors)
        .flat()
        .map(error => typeof error === 'string' ? error : error.message)
        .join(', ');
    }

    return errors.toString();
  }

  /**
   * Show success message
   * @param {string} message - Success message
   */
  static showSuccess(message) {
    toast.success(message);
  }

  /**
   * Show info message
   * @param {string} message - Info message
   */
  static showInfo(message) {
    toast.info(message);
  }

  /**
   * Show warning message
   * @param {string} message - Warning message
   */
  static showWarning(message) {
    toast.warning(message);
  }
}

export default ErrorHandler;
